import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'components';
import MainMenuCommunityItem, {
  type MainMenuCommunityDisableReason,
} from '../MainMenuCommunityItem';
import { CommunityListItem, CommunityRoles } from 'types';
import { MENU_ACTION_ICON_SIZE } from '../styled';

import * as S from './styled';

type DisabledCommunity = {
  community: CommunityListItem;
  disableReason: MainMenuCommunityDisableReason;
};

interface MainMenuDisabledCommunitiesGroupProps {
  items: DisabledCommunity[];
}

type PanelPosition = {
  top: number;
  left: number;
};

function ArchiveGlyph() {
  return (
    <svg
      width={MENU_ACTION_ICON_SIZE}
      height={MENU_ACTION_ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect width="20" height="5" x="2" y="3" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

const panelVariants = {
  hidden: { opacity: 0, x: -8, scale: 0.96 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.22,
      ease: [0.25, 0.1, 0.25, 1],
      staggerChildren: 0.05,
      delayChildren: 0.04,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -14, scale: 0.85 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const MainMenuDisabledCommunitiesGroup: FC<
  MainMenuDisabledCommunitiesGroupProps
> = ({ items }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    setPanelPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  }, []);

  const toggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setExpanded((prev) => {
        const next = !prev;
        if (next) {
          updatePanelPosition();
        }
        return next;
      });
    },
    [updatePanelPosition],
  );

  useEffect(() => {
    if (!expanded) return undefined;

    const onResizeOrScroll = () => updatePanelPosition();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);

    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
    };
  }, [expanded, updatePanelPosition]);

  useEffect(() => {
    if (!expanded) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      const portalRoot = document.getElementById('disabled-communities-panel');
      if (portalRoot?.contains(target)) return;
      setExpanded(false);
    };

    const timerId = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [expanded]);

  if (items.length === 0) {
    return null;
  }

  const tooltipText = t('community.sidebarDisabledGroupTooltip', {
    count: items.length,
  });

  const expandedPanel =
    expanded && panelPosition
      ? createPortal(
          <S.StyledExpandedPanelAnchor
            id="disabled-communities-panel"
            style={{
              top: panelPosition.top,
              left: panelPosition.left,
            }}
          >
            <S.StyledExpandedPanel
              variants={panelVariants}
              initial="hidden"
              animate="visible"
            >
              {items.map(({ community, disableReason }) => (
                <S.StyledExpandedItem
                  key={community.id}
                  variants={itemVariants}
                >
                  <MainMenuCommunityItem
                    isShowTooltip
                    title={community.name}
                    image={community.logo}
                    id={community.id}
                    disableReason={disableReason}
                    invitesCount={community.requests?.length || 0}
                    userRole={
                      community.users?.[0]?.communityPermission?.role ??
                      CommunityRoles.MEMBER
                    }
                  />
                </S.StyledExpandedItem>
              ))}
            </S.StyledExpandedPanel>
          </S.StyledExpandedPanelAnchor>,
          document.body,
        )
      : null;

  return (
    <S.StyledGroupRoot ref={rootRef}>
      <Tooltip isVisible text={tooltipText} position="right-center">
        <S.StyledTriggerWrap>
          <S.StyledGroupTrigger
            ref={triggerRef}
            type="button"
            $expanded={expanded}
            aria-expanded={expanded}
            aria-label={tooltipText}
            onClick={toggle}
          >
            <ArchiveGlyph />
          </S.StyledGroupTrigger>
        </S.StyledTriggerWrap>
      </Tooltip>
      {expandedPanel}
    </S.StyledGroupRoot>
  );
};

export default MainMenuDisabledCommunitiesGroup;
